import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { setCanonical, setMeta } from '@/utils/seo';
import { SpeakableSchema, BreadcrumbSchema } from '@/components/SchemaMarkup';
import { Shield, Users, Heart, DollarSign, Phone, Mail, MapPin, Clock, Star, ExternalLink } from 'lucide-react';
import HeroBackground from '@/components/HeroBackground';
import LeadForm from '@/components/LeadForm';

const REVIEWS = [
  { text: "Trio Insurance helped us find workers comp coverage that actually fit our construction business. We were overpaying for years and didn't even know it. Their team compared rates from multiple carriers and saved us over $4,000 annually.", name: "Marco R.", source: "Google Review" },
  { text: "As a restaurant owner, I was overwhelmed trying to figure out workers comp requirements. Trio's agents walked me through everything, explained the class codes, and found me a great rate. Professional and knowledgeable.", name: "Lisa T.", source: "Google Review" },
  { text: "Awesome experience with this brokerage especially with the agent, Tom Pastor. Excellent customer service. They saved me and all my friends and relatives that I referred to them so much money on our auto, home and life insurance.", name: "Fernando S.", source: "Google Review" }
];

export default function AboutPage() {
  useEffect(() => {
    setCanonical('/about');
    setMeta('About CompareWorkersComp.com | Trio Insurance & Financial Services', 'Learn about CompareWorkersComp.com, operated by Trio Insurance & Financial Services. Over 100 years of collective experience protecting California businesses.');
  }, []);

  return (
    <>
      <SpeakableSchema name="About CompareWorkersComp.com" url="https://compareworkerscomp.com/about" />
      <BreadcrumbSchema items={[{ name: 'Home', url: '/' }, { name: 'About' }]} />

      {/* Hero */}
      <section className="relative pt-24 pb-16 overflow-hidden">
        <HeroBackground />
        <div className="relative z-10 max-w-4xl mx-auto px-4 text-center">
          <p className="speakable-intro text-white/30 text-xs mb-3">Trio Insurance & Financial Services helps California business owners compare workers compensation insurance from over 50 carriers. Licensed in California (License #0C66738), Trio serves employers across all industries statewide.</p>
          <h1 className="text-3xl md:text-5xl font-extrabold text-white hero-text-shadow mb-3" data-testid="about-h1">About Our Agency</h1>
          <p className="text-white/70 text-lg hero-text-shadow-sm">A trusted provider of workers compensation insurance solutions<br />with over a hundred years of collective experience</p>
        </div>
      </section>

      {/* Our Agency */}
      <section className="bg-white py-16">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-2xl md:text-3xl font-bold text-cwc-navy mb-6">Your Workers Comp Specialists in California</h2>
              <p className="text-gray-600 leading-relaxed mb-4">At Trio Insurance & Financial Services in the San Francisco Bay Area, we prioritize the safety and security of your employees, assets, and business operations. With our extensive expertise and experience, we are equipped to handle the diverse risks and challenges associated with workers compensation insurance, ensuring you can focus on running your business.</p>
              <p className="text-gray-600 leading-relaxed mb-4">CompareWorkersComp.com is our specialized platform for California workers compensation insurance. We shop among 50+ carriers to find you the best coverage and rates for your specific industry — whether you run a construction company, restaurant, home health agency, landscaping business, trucking operation, or any other California business.</p>
              <p className="text-gray-600 leading-relaxed">We believe that workers comp insurance should be accessible and easy to understand. Our knowledgeable staff is always available to answer questions and help you find the right coverage for your individual needs.</p>
            </div>
            <div>
              <img src="/images/team.jpg" alt="Trio Insurance team" className="rounded-2xl shadow-lg w-full" loading="lazy" />
            </div>
          </div>
        </div>
      </section>

      {/* Value Pillars */}
      <section className="bg-gray-50 py-16">
        <div className="max-w-5xl mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            {[
              { Icon: Shield, title: 'Comprehensive Coverage', desc: 'We cover all 6 major California industries with tailored workers comp solutions.' },
              { Icon: Users, title: 'Experienced Team', desc: 'Our team comprises insurance experts with over a hundred years of collective experience.' },
              { Icon: Heart, title: 'Commitment to Clients', desc: "We're committed to being there for our clients at every step of the way." },
              { Icon: DollarSign, title: 'Affordable Rates', desc: 'We shop 50+ carriers to find the best coverage at competitive prices for your budget.' }
            ].map(({ Icon, title, desc }) => (
              <div key={title} className="bg-white rounded-xl p-6 text-center border border-gray-100 shadow-sm">
                <div className="w-12 h-12 bg-red-50 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Icon className="w-6 h-6 text-red-600" />
                </div>
                <h3 className="font-bold text-cwc-navy mb-2 text-sm">{title}</h3>
                <p className="text-gray-500 text-xs leading-relaxed">{desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Mission */}
      <section className="bg-cwc-navy py-14">
        <div className="max-w-3xl mx-auto px-4 text-center">
          <h2 className="text-2xl font-bold text-white mb-4">Our Mission</h2>
          <p className="text-white/70 leading-relaxed">Located in the San Francisco Bay Area, Trio Insurance & Financial Services prioritizes safety through risk management. Our team functions like a family to ensure adequate compensation for both employees and business owners when needed. We provide affordable coverage and guidance and promise to deliver on our commitment to protect you.</p>
        </div>
      </section>

      {/* Google Reviews */}
      <section className="bg-white py-16">
        <div className="max-w-5xl mx-auto px-4">
          <h2 className="text-2xl font-bold text-cwc-navy text-center mb-10">What Our Clients Say on Google</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {REVIEWS.map((r, i) => (
              <div key={i} className="bg-gray-50 rounded-xl p-6 border border-gray-100">
                <div className="flex gap-1 mb-3">
                  {[1,2,3,4,5].map(s => <Star key={s} className="w-4 h-4 fill-yellow-400 text-yellow-400" />)}
                </div>
                <p className="text-gray-600 text-sm leading-relaxed mb-4 italic">"{r.text}"</p>
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 rounded-full bg-cwc-navy text-white flex items-center justify-center text-xs font-bold">{r.name[0]}</div>
                  <div>
                    <p className="text-cwc-navy font-semibold text-sm">{r.name}</p>
                    <p className="text-gray-400 text-xs">{r.source}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Carriers */}
      <section className="bg-gray-50 py-12">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2 className="text-xl font-bold text-cwc-navy mb-6">Our Carrier Partners</h2>
          <img src="/carriers.png" alt="Insurance Carriers" className="max-w-full mx-auto" loading="lazy" />
        </div>
      </section>

      {/* For Other Coverage — cross-link to TrioInsureMe */}
      <section className="bg-white py-12">
        <div className="max-w-3xl mx-auto px-4 text-center">
          <div className="bg-cwc-navy rounded-2xl p-8">
            <img src="/trio-logo.png" alt="Trio Insurance" className="h-10 mx-auto mb-4" />
            <h2 className="text-xl font-bold text-white mb-2">Need Other Coverage?</h2>
            <p className="text-white/60 mb-6 text-sm">Trio Insurance also offers Auto, Home, Business, Life, and 20+ other insurance types across California.</p>
            <a href="https://trioinsureme.com" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 bg-red-600 text-white font-semibold px-6 py-3 rounded-full hover:bg-red-700 transition-colors text-sm" data-testid="trio-crosslink">
              Visit Trio Insurance <ExternalLink className="w-4 h-4" />
            </a>
          </div>
        </div>
      </section>

      {/* We Are Here to Help + Contact Form */}
      <section className="bg-gray-50 py-16" id="contact-form">
        <div className="max-w-6xl mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            <div>
              <h2 className="text-2xl font-bold text-cwc-navy mb-4">We Are Here to Help</h2>
              <p className="text-gray-600 leading-relaxed mb-8">Contact Trio Insurance & Financial Services today to see how we can help you protect your employees and your business. Our licensed agents are ready to find you competitive workers comp rates from 50+ carriers.</p>
              <div className="space-y-4">
                <a href="tel:+14159282886" className="flex items-center gap-3 text-cwc-navy hover:text-red-600 transition-colors">
                  <div className="w-10 h-10 bg-red-50 rounded-full flex items-center justify-center"><Phone className="w-5 h-5 text-red-600" /></div>
                  <span className="font-medium">(415) 928-2886</span>
                </a>
                <a href="mailto:info@trioins.com" className="flex items-center gap-3 text-cwc-navy hover:text-red-600 transition-colors">
                  <div className="w-10 h-10 bg-red-50 rounded-full flex items-center justify-center"><Mail className="w-5 h-5 text-red-600" /></div>
                  <span className="font-medium">info@trioins.com</span>
                </a>
                <div className="flex items-center gap-3 text-cwc-navy">
                  <div className="w-10 h-10 bg-red-50 rounded-full flex items-center justify-center"><MapPin className="w-5 h-5 text-red-600" /></div>
                  <span className="font-medium">San Francisco Bay Area, CA</span>
                </div>
                <div className="flex items-center gap-3 text-cwc-navy">
                  <div className="w-10 h-10 bg-red-50 rounded-full flex items-center justify-center"><Clock className="w-5 h-5 text-red-600" /></div>
                  <span className="font-medium">Mon-Fri: 9:00 AM - 6:00 PM</span>
                </div>
              </div>
            </div>
            <div>
              <LeadForm source="about-page" title="Contact Us" />
            </div>
          </div>
        </div>
      </section>

      {/* Featured In */}
      <section className="bg-white py-12">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <p className="text-sm font-bold text-cwc-navy mb-4">Trio Insurance Featured In</p>
          <img src="/featured-in.png" alt="Featured in FOX, Google News, Digital Journal" className="max-w-sm mx-auto" loading="lazy" />
        </div>
      </section>
    </>
  );
}
